import React, { useCallback, useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { BACKEND } from '../config';
import { useAuth } from '../contexts/AuthContext';
import TopBar from '../components/TopBar/TopBar';
import { ModeToggle } from '../components/ModeToggle';
import { formatDate, formatDateTime, formatMoney, formatNullable } from '../utils/format';
import { csrfFetch } from '../utils/csrfFetch';
import styles from './RepresentativeCasePage.module.css';

type Registry = {
  id: number;
  title: string;
  year: number;
  source_excel_name: string;
  created_at: string;
  debtors_count: number;
};

type Debtor = {
  id: number;
  fio: string;
  iin: string;
  dogovor: string;
  phone: string;
  amount: string;
  raw_data: Record<string, any> | null;
};

type PaymentLog = {
  id: number;
  file_name: string;
  created_at: string;
  uploaded_by: string;
};

const RepresentativeCasePage: React.FC = () => {
  const { role } = useAuth();
  const [registries, setRegistries] = useState<Registry[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [q, setQ] = useState('');
  const [searchMode, setSearchMode] = useState<'debtor' | 'registry'>('registry');
  const [selectedRegistry, setSelectedRegistry] = useState<Registry | null>(null);
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [logs, setLogs] = useState<PaymentLog[]>([]);

  const [showAddRegistry, setShowAddRegistry] = useState(false);
  const [showDeleteRegistry, setShowDeleteRegistry] = useState(false);
  const [registryTitle, setRegistryTitle] = useState('');
  const [registryYear, setRegistryYear] = useState('');
  const [registryFile, setRegistryFile] = useState<File | null>(null);
  const [deleteRegistryId, setDeleteRegistryId] = useState<number | ''>('');

  const [editingDebtor, setEditingDebtor] = useState<Debtor | null>(null);
  const canAccessCasePage = role === 'chairman' || role === 'representative' || role === 'representative_employee';

  const loadRegistries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (selectedYear) params.set('year', selectedYear);
      const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/?${params.toString()}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки реестров');
      setRegistries(Array.isArray(data.registries) ? data.registries : []);
      setAvailableYears(Array.isArray(data.available_years) ? data.available_years : []);
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка загрузки реестров');
    }
  }, [q, selectedYear]);

  const loadDebtors = async (registryId: number, search: string = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/${registryId}/debtors/?${params.toString()}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки должников');
      setDebtors(Array.isArray(data.debtors) ? data.debtors : []);
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка загрузки должников');
    }
  };

  const loadLogs = async (registryId: number) => {
    try {
      const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/${registryId}/payments/`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Ошибка загрузки логов выплат');
      setLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (error: any) {
      setLogs([]);
      toast.error(error?.message || 'Ошибка загрузки логов выплат');
    }
  };

  useEffect(() => {
    if (canAccessCasePage) {
      loadRegistries();
    }
  }, [canAccessCasePage, loadRegistries]);

  useEffect(() => {
    if (!selectedRegistry && registries.length) {
      const latest = [...registries].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      setSelectedRegistry(latest);
    }
  }, [registries, selectedRegistry]);

  useEffect(() => {
    if (selectedRegistry) {
      loadDebtors(selectedRegistry.id);
      loadLogs(selectedRegistry.id);
    }
  }, [selectedRegistry]);

  const addRegistry = async () => {
    try {
      const fd = new FormData();
      if (registryTitle) fd.append('title', registryTitle);
      if (registryYear) fd.append('year', registryYear);
      if (registryFile) fd.append('file', registryFile);

      const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Ошибка добавления реестра');
      toast.success('Реестр добавлен');
      setShowAddRegistry(false);
      setRegistryTitle('');
      setRegistryYear('');
      setRegistryFile(null);
      loadRegistries();
    } catch (error: any) {
      toast.error(error?.message || 'Ошибка добавления реестра');
    }
  };

  const deleteRegistry = async () => {
    if (!deleteRegistryId) return;
    if (!window.confirm('Вы уверены что хотите удалить реестр?')) return;
    const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/${deleteRegistryId}/`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json();
    if (!res.ok) return toast.error(data?.error || 'Ошибка удаления реестра');
    toast.success('Реестр удален');
    setShowDeleteRegistry(false);
    setDeleteRegistryId('');
    if (selectedRegistry?.id === deleteRegistryId) setSelectedRegistry(null);
    loadRegistries();
  };

  const deleteDebtor = async (debtorId: number) => {
    if (!window.confirm('Удалить данные по должнику?')) return;
    const res = await csrfFetch(`${BACKEND}/api/representative/case/debtors/${debtorId}/`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json();
    if (!res.ok) return toast.error(data?.error || 'Ошибка удаления должника');
    toast.success('Должник удален');
    if (selectedRegistry) loadDebtors(selectedRegistry.id, q);
  };

  const saveDebtor = async () => {
    if (!editingDebtor) return;
    const res = await csrfFetch(`${BACKEND}/api/representative/case/debtors/${editingDebtor.id}/`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingDebtor),
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data?.error || 'Ошибка редактирования должника');
    toast.success('Должник обновлен');
    setEditingDebtor(null);
    if (selectedRegistry) loadDebtors(selectedRegistry.id, q);
  };

  const uploadPayments = async (file: File) => {
    if (!selectedRegistry) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await csrfFetch(`${BACKEND}/api/representative/case/registries/${selectedRegistry.id}/payments/`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) return toast.error(data?.error || 'Ошибка загрузки выплат');
    toast.success('Выплаты загружены');
    loadLogs(selectedRegistry.id);
  };

  if (!canAccessCasePage) return <div>Доступ ограничен.</div>;

  return (
    <div className={styles.page}>
      <TopBar />
      <ToastContainer />

      <div className={styles.header}>
        <h1>Дело</h1>
        <p className={styles.subtitle}>Интерфейс в стиле страницы «Досье» для работы с реестрами и должниками.</p>
      </div>

      <div className={styles.searchCard}>
        <div className={styles.workModeRow}>
          <span className={styles.searchModeLabel}>Режим работы:</span>
          <div className={styles.actionsRow}>
            <button className={styles.workActionBtn} onClick={() => setShowAddRegistry(true)}>Добавить реестр</button>
            <button className={styles.workActionBtnDanger} onClick={() => setShowDeleteRegistry(true)} disabled={!registries.length}>Удалить реестр</button>
          </div>
        </div>

        <ModeToggle
          modes={[
            { value: 'debtor', label: 'Должники' },
            { value: 'registry', label: 'Реестры' },
          ]}
          current={searchMode}
          onChange={(mode) => setSearchMode(mode)}
          styles={styles}
        />

        {searchMode === 'registry' && (
          <div className={styles.yearRow}>
            <span className={styles.searchModeLabel}>Год реестра:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className={styles.filterSelect}
              style={{ minWidth: 140 }}
            >
              <option value="">Все</option>
              {availableYears.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>
        )}

        <div className={styles.searchRow}>
          <input
            className={`${styles.textInput} ${styles.searchInput}`}
            placeholder="Поиск по ФИО / ИИН / договору"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className={styles.btn} onClick={loadRegistries}>Поиск</button>
          <button className={styles.btnGhost} onClick={() => { setQ(''); loadRegistries(); }}>Сбросить</button>
        </div>

      </div>

      <div className={styles.registryList}>
        {!registries.length && <div className={styles.emptyState}>Реестров пока нет</div>}

        {registries.map((r) => {
          const isSelected = selectedRegistry?.id === r.id;
          return (
            <article key={r.id} className={`${styles.registryCard} ${isSelected ? styles.registryCardActive : ''}`}>
              <div className={styles.registryHeader}>
                <div>
                  <div className={styles.registryTitle}>{r.title}</div>
                  <div className={styles.registryMeta}>
                    {formatDate(r.created_at)} · {r.year} · Должников: {r.debtors_count}
                  </div>
                </div>
                <div className={styles.registryActions}>
                  <button className={styles.iconButton} onClick={() => setSelectedRegistry(r)} title="Открыть">👁</button>
                  {isSelected && (
                    <label className={styles.fileUploadInline}>
                      <span className={styles.btnGhost}>Загрузить выплаты</span>
                      <input
                        className={styles.fileInputHidden}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => e.target.files?.[0] && uploadPayments(e.target.files[0])}
                      />
                    </label>
                  )}
                </div>
              </div>

              {isSelected && (
                <div className={styles.debtorsSection}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>ФИО</th>
                        <th>ИИН</th>
                        <th>Договор</th>
                        <th>Сумма</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debtors.map((d) => (
                        <tr key={d.id}>
                          <td>{formatNullable(d.fio)}</td>
                          <td>{formatNullable(d.iin)}</td>
                          <td>{formatNullable(d.dogovor)}</td>
                          <td>{formatMoney(d.amount)}</td>
                          <td>
                            <div className={styles.rowActions}>
                              <button className={styles.actionButton} onClick={() => setEditingDebtor(d)}>Редактировать</button>
                              <button className={styles.actionButtonDanger} onClick={() => deleteDebtor(d.id)}>Удалить</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!debtors.length && <tr><td className={styles.emptyCell} colSpan={5}>Нет данных</td></tr>}
                    </tbody>
                  </table>

                  <h4>Логи выплат</h4>
                  <ul className={styles.logList}>
                    {logs.map((log) => (
                      <li className={styles.logItem} key={log.id}>{formatDateTime(log.created_at)} — {log.file_name} ({log.uploaded_by})</li>
                    ))}
                    {!logs.length && <li className={styles.logItem}>Логов пока нет</li>}
                  </ul>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {showAddRegistry && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Добавить реестр</h3>
            <div className={styles.modalBody}>
            <input className={styles.textInput} placeholder="Название" value={registryTitle} onChange={(e) => setRegistryTitle(e.target.value)} />
            <input className={styles.textInput} placeholder="Год" value={registryYear} onChange={(e) => setRegistryYear(e.target.value)} />
            <input className={styles.fileInput} type="file" accept=".xlsx,.xls" onChange={(e) => setRegistryFile(e.target.files?.[0] || null)} />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnGhost} onClick={() => setShowAddRegistry(false)}>Отмена</button>
              <button className={styles.btn} onClick={addRegistry}>Загрузить</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteRegistry && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Удалить реестр</h3>
            <select className={styles.select} value={deleteRegistryId} onChange={(e) => setDeleteRegistryId(Number(e.target.value) || '')}>
              <option value="">Выберите реестр</option>
              {registries.map((r) => (
                <option value={r.id} key={r.id}>{r.title} ({r.year})</option>
              ))}
            </select>
            <div className={styles.modalActions}>
              <button className={styles.btnGhost} onClick={() => setShowDeleteRegistry(false)}>Отмена</button>
              <button className={styles.btnDanger} onClick={deleteRegistry} disabled={!deleteRegistryId}>Удалить</button>
            </div>
          </div>
        </div>
      )}

      {editingDebtor && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Редактировать должника</h3>
            <div className={styles.modalBody}>
            <input className={styles.textInput} value={editingDebtor.fio} onChange={(e) => setEditingDebtor({ ...editingDebtor, fio: e.target.value })} />
            <input className={styles.textInput} value={editingDebtor.iin} onChange={(e) => setEditingDebtor({ ...editingDebtor, iin: e.target.value })} />
            <input className={styles.textInput} value={editingDebtor.dogovor} onChange={(e) => setEditingDebtor({ ...editingDebtor, dogovor: e.target.value })} />
            <input className={styles.textInput} value={editingDebtor.phone} onChange={(e) => setEditingDebtor({ ...editingDebtor, phone: e.target.value })} />
            <input className={styles.textInput} value={editingDebtor.amount} onChange={(e) => setEditingDebtor({ ...editingDebtor, amount: e.target.value })} />
            <textarea
              className={styles.textArea}
              value={JSON.stringify(editingDebtor.raw_data || {}, null, 2)}
              onChange={(e) => {
                try {
                  setEditingDebtor({ ...editingDebtor, raw_data: JSON.parse(e.target.value || '{}') });
                } catch {
                  // ignore invalid json until save
                }
              }}
              rows={8}
            />
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnGhost} onClick={() => setEditingDebtor(null)}>Отмена</button>
              <button className={styles.btn} onClick={saveDebtor}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepresentativeCasePage;
